import { useId, useState, type ReactNode } from 'react';
import { FiltreBarPortal, useFiltreBarYuvasi } from '../FiltreBarYuvasi';
import { useDarEkran } from './useDarEkran';

interface RaporFiltreBariProps {
  /**
   * Ekranın ANA seçicisi (segment). Telefonda yapışık barda yalnız bu kalır; masaüstünde barın ilk
   * öğesidir.
   */
  birincil?: ReactNode;
  /** Öteki filtre alanları. Masaüstünde barda; telefonda sayfa içinde, KAPALI başlayan "Filtreler" altında. */
  children?: ReactNode;
  /** Telefonda kapalı panelin rozeti: sonucu KISITLAYAN filtrelerin adları ("1 etkin"). */
  etkinAdlar?: readonly string[];
  /** Telefonda "Filtreler" başlığının yanında yazan kısa özet (ör. "96 cari · 01.10.2026 tarihine kadar"). */
  ozet?: string;
  testid?: string;
}

/**
 * S334: rapor ekranlarının filtre barı. Header'ın hemen ALTINDAKİ yapışık yuvaya basar (G2b kalıbı,
 * `pages/StokAnalizi.tsx` ile aynı kabuk: yüzey zemin, alt kenarlık, içerik hizası Layout'unki).
 * Yuva yoksa (izole kabuk) sayfa içinde yapışık şerit olur.
 *
 * TELEFON (<= 767 px), Yerlice kalıbı: yapışık barda yalnız ana seçici durur (Yerlice `FiltreBar`:
 * dönem sekmeleri); geri kalan filtreler sayfa içinde KATLANIR ve kapalı başlar, başlıkta etkin filtre
 * sayısı yazar (Yerlice `KatlanirFiltre`: açık panel tablonun üstünde 312 px tutuyordu; rozet olmadan
 * gizli filtreyle yanlış rakam okunur). Kapalıyken alanlar DOM'da durmaz; değerleri sayfada yaşar.
 */
export function RaporFiltreBari({ birincil, children, etkinAdlar = [], ozet, testid = 'rapor-filtre-bar' }: RaporFiltreBariProps) {
  const { hedef } = useFiltreBarYuvasi();
  const telefon = useDarEkran();
  const [acik, setAcik] = useState(false);
  const govdeId = useId();

  const satir = (icerik: ReactNode) => <div className="flex flex-wrap items-end gap-x-3.5 gap-y-2 min-w-0">{icerik}</div>;
  const bar = (icerik: ReactNode) =>
    hedef === null ? (
      <div data-testid={`${testid}-dar`} className={`sticky top-0 z-10 bg-page py-2${telefon ? ' rf-telefon' : ''}`}>
        {satir(icerik)}
      </div>
    ) : (
      <FiltreBarPortal>
        <div
          data-testid={testid}
          className={`bg-surface border-b border-border shadow-[0_1px_3px_rgb(16_33_92/0.06)]${telefon ? ' rf-telefon' : ''}`}
        >
          <div className="mx-auto w-full max-w-[1400px] px-4 md:px-6 py-2">{satir(icerik)}</div>
        </div>
      </FiltreBarPortal>
    );

  if (!telefon) return bar(<>{birincil}{children}</>);

  return (
    <>
      {birincil ? bar(birincil) : null}
      {children ? (
        <div data-testid={`${testid}-katlanir`}>
          <button
            type="button"
            className="rf-tetik"
            data-testid={`${testid}-tetik`}
            aria-expanded={acik}
            aria-controls={govdeId}
            aria-label={etkinAdlar.length > 0 ? `Filtreler, etkin: ${etkinAdlar.join(', ')}` : 'Filtreler'}
            onClick={() => setAcik((a) => !a)}
          >
            <span>Filtreler</span>
            {etkinAdlar.length > 0 && <span className="rf-rozet" data-testid={`${testid}-rozet`}>{etkinAdlar.length} etkin</span>}
            {ozet && <span className="rf-ozet" data-testid={`${testid}-ozet`}>{ozet}</span>}
            <span className="rf-ok" aria-hidden="true">{acik ? '▲' : '▼'}</span>
          </button>
          {acik && <div id={govdeId} className="pb-1" data-testid={`${testid}-govde`}>{satir(children)}</div>}
        </div>
      ) : null}
    </>
  );
}

const ETIKET = 'rf-alan-etiket text-[11px] uppercase tracking-[0.04em] text-ink-500 leading-none';

/**
 * Etiket üstte, kontrol altta. Tek bir giriş alanını sarıyorsa `<label>` (etikete basmak alanı odaklar);
 * düğme grubunu sarıyorsa `grup` verilir ve `role="group"` olur (label birden çok düğmeyi adlandıramaz).
 */
export function FiltreAlani({ etiket, grup = false, children }: { etiket: string; grup?: boolean; children: ReactNode }) {
  if (grup) {
    return (
      <div role="group" aria-label={etiket} className="flex flex-col gap-1 min-w-0">
        <span className={ETIKET}>{etiket}</span>
        {children}
      </div>
    );
  }
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className={ETIKET}>{etiket}</span>
      {children}
    </label>
  );
}

/**
 * Filtre barındaki küçük giriş alanı (tarih, arama): 32 px. Telefonda 44 px dokunma hedefi; yazı boyu
 * orada sınıfla VERİLMEZ, `tailwind.css`teki 16 px kuralı geçerli kalır (iOS 16 px altı girişe
 * odaklanınca sayfayı yakınlaştırır).
 */
export const FILTRE_GIRIS =
  'h-8 max-md:h-11 min-w-0 rounded-md border border-border-strong bg-surface px-2 md:text-[13px] text-ink-900 ' +
  'placeholder:text-ink-400 outline-none focus:border-brand-600 focus:shadow-[0_0_0_3px_rgba(29,78,216,0.12)]';

/** 'YYYY-MM-DD' ve makul bir yıl mı? Elle yazarken ara değerler ("0002-01-01") sorguya gitmesin. */
export function gecerliTarih(v: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const yil = Number(m[1]);
  return yil >= 1990 && yil <= 2100;
}
